/**
 * Admin Setup → OTD Rules tab (CR-5263).
 *
 * Surfaces the four platform-wide OTD calculation toggles that drive
 * `purchasing.services.otd_service.OTDRules` on the backend. Each toggle
 * patches PlatformSettings on change so saves are inline (no separate
 * Save button) — matches the pattern used on the Quality tab.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Loader2, Info } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

type OTDFlag =
  | 'otd_include_on_hold'
  | 'otd_hold_suspends_promise_change_impact'
  | 'otd_promise_change_off_hold_counts'
  | 'otd_allow_line_level_override'
  | 'otd_apply_retroactively';

interface OTDState {
  otd_hold_suspends_promise_change_impact: boolean;
  otd_promise_change_off_hold_counts: boolean;
  otd_allow_line_level_override: boolean;
  otd_include_on_hold: boolean;
  otd_apply_retroactively: boolean;
}

const INITIAL: OTDState = {
  otd_hold_suspends_promise_change_impact: true,
  otd_promise_change_off_hold_counts: true,
  otd_allow_line_level_override: true,
  otd_include_on_hold: false,
  otd_apply_retroactively: false,
};

const ROW_META: Array<{
  key: OTDFlag;
  title: string;
  description: string;
}> = [
  {
    key: 'otd_include_on_hold',
    title: 'Include lines on hold in OTD',
    description:
      'When OFF (default), a line on hold is left out of OTD entirely — a hold is not '
      + 'counted as the site missing a date. When ON, held lines are measured like any '
      + 'other, so a hold running past the promised date counts as late. Applies to the '
      + 'donut, the drill-down, the stored per-line verdict and Site Performance alike. '
      + 'Switching it on changes reported OTD until the nightly recompute catches up.',
  },
  {
    key: 'otd_hold_suspends_promise_change_impact',
    title: 'Hold suspends promise-date change impact',
    description:
      'When ON, a promise-date change made while the line is on hold does NOT shift the OTD target date. Recommended.',
  },
  {
    key: 'otd_promise_change_off_hold_counts',
    title: 'Off-hold promise change counts toward OTD',
    description:
      'When ON, a promise-date change made while the line is NOT on hold DOES shift the OTD target date.',
  },
  {
    key: 'otd_allow_line_level_override',
    title: 'Allow line-level manual override',
    description:
      'When ON, buyers may force OTD on/off for a specific line via the PO Line detail panel. Use sparingly — overrides bypass all rule logic.',
  },
  {
    key: 'otd_apply_retroactively',
    title: 'Apply rules retroactively to historic GRNs',
    description:
      'When OFF, the rules above only affect GRNs received after rollout. When ON, all historic OTD figures are recomputed.',
  },
];

export function OTDRulesTab() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<OTDState>(INITIAL);
  const [pendingKey, setPendingKey] = useState<OTDFlag | null>(null);
  // OTD grace buffer (days). Stored as a string for the controlled input;
  // `graceSaved` is the last persisted value so we can show a dirty state.
  const [graceDays, setGraceDays] = useState<string>('1');
  const [graceSaved, setGraceSaved] = useState<number>(1);
  const [graceSaving, setGraceSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await platformSettingsAPI.getSettings();
        if (cancelled) return;
        const d = res.data;
        setState({
          otd_hold_suspends_promise_change_impact:
            d.otd_hold_suspends_promise_change_impact ?? true,
          otd_promise_change_off_hold_counts:
            d.otd_promise_change_off_hold_counts ?? true,
          otd_allow_line_level_override:
            d.otd_allow_line_level_override ?? true,
          otd_apply_retroactively: d.otd_apply_retroactively ?? false,
          otd_include_on_hold: d.otd_include_on_hold ?? false,
        });
        const g = Number(d.otd_grace_days ?? 1);
        const gv = Number.isFinite(g) ? Math.max(0, Math.min(14, Math.round(g))) : 1;
        setGraceDays(String(gv));
        setGraceSaved(gv);
      } catch (err: unknown) {
        console.error('Failed to load OTD rules', err);
        toast.error('Failed to load OTD rules');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (key: OTDFlag, next: boolean) => {
    const prev = state[key];
    if (prev === next) return;
    setState((s) => ({ ...s, [key]: next }));
    setPendingKey(key);
    try {
      await platformSettingsAPI.patchSettings({ [key]: next });
      toast.success('OTD rule updated');
    } catch (err: unknown) {
      console.error('Failed to save OTD rule', err);
      toast.error('Failed to save OTD rule');
      setState((s) => ({ ...s, [key]: prev }));
    } finally {
      setPendingKey(null);
    }
  };

  const saveGrace = async () => {
    const parsed = Math.max(0, Math.min(14, Math.round(Number(graceDays))));
    if (!Number.isFinite(parsed)) {
      toast.error('Enter a whole number of days (0–14)');
      setGraceDays(String(graceSaved));
      return;
    }
    if (parsed === graceSaved) {
      setGraceDays(String(parsed));
      return;
    }
    setGraceSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ otd_grace_days: parsed });
      setGraceSaved(parsed);
      setGraceDays(String(parsed));
      toast.success(`OTD grace buffer set to ${parsed} day${parsed === 1 ? '' : 's'}`);
    } catch (err: unknown) {
      console.error('Failed to save OTD grace buffer', err);
      toast.error('Failed to save OTD grace buffer');
      setGraceDays(String(graceSaved));
    } finally {
      setGraceSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const graceDirty = String(graceSaved) !== graceDays.trim();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>OTD grace buffer</CardTitle>
          <p className="text-sm text-muted-foreground">
            How many days late a delivery may be and still count as
            <span className="font-medium"> On-Time</span>. A buffer of 1 means a
            dispatch one day after the promised date is still on-time; only day
            two onward is Late. Set to 0 for strict scoring. Applies across the
            OTD %, the donut, the Deliveries-by-Day chart and every drill-down.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="otd-grace-days" className="text-sm font-medium">
                Allowed buffer (days)
              </Label>
              <Input
                id="otd-grace-days"
                type="number"
                min={0}
                max={14}
                step={1}
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                onBlur={saveGrace}
                onKeyDown={(e) => { if (e.key === 'Enter') saveGrace(); }}
                className="w-28"
                disabled={graceSaving}
              />
            </div>
            <Button
              onClick={saveGrace}
              disabled={graceSaving || !graceDirty}
              size="sm"
            >
              {graceSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>OTD calculation rules</CardTitle>
          <p className="text-sm text-muted-foreground">
            Platform-wide rules that drive how the dashboard calculates
            On-Time Delivery. Changes apply on next dashboard refresh.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {ROW_META.map((row) => (
            <div
              key={row.key}
              className="flex items-start justify-between gap-6 border-b last:border-b-0 pb-4 last:pb-0"
            >
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor={`otd-${row.key}`}
                  className="text-sm font-medium"
                >
                  {row.title}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {row.description}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {pendingKey === row.key ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : null}
                <Switch
                  id={`otd-${row.key}`}
                  checked={state[row.key]}
                  onCheckedChange={(v) => toggle(row.key, v)}
                  disabled={pendingKey !== null && pendingKey !== row.key}
                />
              </div>
            </div>
          ))}
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>
              Rule-driven automatic recomputation (replaying promise-date
              change history with hold-state at the moment of each change)
              is rolling out in a follow-up release. The line-level manual
              override on the PO Line detail panel works today.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
